import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Property } from '@/types/property';

interface PriceDistributionChartProps {
  properties: Property[];
  isMobile?: boolean;
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana';
}

interface PriceRange {
  range: string;
  count: number;
}

const PriceDistributionChart: React.FC<PriceDistributionChartProps> = ({ 
  properties, 
  isMobile = false,
  dashboardType = 'venda'
}) => {
  const calculatePriceRanges = (): PriceRange[] => {
    if (!properties || properties.length === 0) return [];

    const prices = properties
      .map(property => {
        const price = typeof property.valor_recente === 'string'
          ? parseFloat(property.valor_recente.replace(/[^\d.-]/g, ''))
          : property.valor_recente;
        return isNaN(price) ? 0 : price;
      })
      .filter(price => price > 0);

    if (prices.length === 0) return [];

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // Range size differs for sales and rentals
    let rangeSize: number;
    let ranges: { min: number; max: number; label: string }[];

    if (dashboardType === 'aluguel') {
      // For rentals, smaller ranges
      rangeSize = Math.ceil((maxPrice - minPrice) / 8);
      
      // Define custom ranges for rentals
      if (maxPrice <= 10000) {
        ranges = [
          { min: 0, max: 1000, label: 'R$0 - R$1.000' },
          { min: 1000, max: 2000, label: 'R$1.000 - R$2.000' },
          { min: 2000, max: 3000, label: 'R$2.000 - R$3.000' },
          { min: 3000, max: 4000, label: 'R$3.000 - R$4.000' },
          { min: 4000, max: 5000, label: 'R$4.000 - R$5.000' },
          { min: 5000, max: 7500, label: 'R$5.000 - R$7.500' },
          { min: 7500, max: 10000, label: 'R$7.500 - R$10.000' },
          { min: 10000, max: Infinity, label: '> R$10.000' },
        ];
      } else {
        // For higher-priced rentals
        rangeSize = Math.ceil((maxPrice - minPrice) / 8);
        ranges = Array.from({ length: 8 }, (_, i) => {
          const min = minPrice + rangeSize * i;
          const max = i === 7 ? Infinity : minPrice + rangeSize * (i + 1);
          return {
            min,
            max,
            label: i === 7 
              ? `> R$${Math.floor(min / 1000)}k` 
              : `R$${Math.floor(min / 1000)}k - R$${Math.floor(max / 1000)}k`
          };
        });
      }
    } else {
      // For sales, larger ranges
      rangeSize = Math.ceil((maxPrice - minPrice) / 7);
      
      // Define custom ranges for sales
      if (maxPrice <= 5000000) {
        ranges = [
          { min: 0, max: 500000, label: 'R$0 - R$500k' },
          { min: 500000, max: 1000000, label: 'R$500k - R$1M' },
          { min: 1000000, max: 1500000, label: 'R$1M - R$1,5M' },
          { min: 1500000, max: 2000000, label: 'R$1,5M - R$2M' },
          { min: 2000000, max: 3000000, label: 'R$2M - R$3M' },
          { min: 3000000, max: 5000000, label: 'R$3M - R$5M' },
          { min: 5000000, max: Infinity, label: '> R$5M' },
        ];
      } else {
        // For higher-priced properties
        rangeSize = Math.ceil((maxPrice - minPrice) / 7);
        ranges = Array.from({ length: 7 }, (_, i) => {
          const min = minPrice + rangeSize * i;
          const max = i === 6 ? Infinity : minPrice + rangeSize * (i + 1);
          return {
            min,
            max,
            label: i === 6 
              ? `> R$${Math.floor(min / 1000000)}M` 
              : `R$${Math.floor(min / 1000000)}M - R$${Math.floor(max / 1000000)}M`
          };
        });
      }
    }

    return ranges.map(range => ({
      range: range.label,
      count: prices.filter(price => price >= range.min && price < range.max).length
    }));
  };

  const data = useMemo(calculatePriceRanges, [properties, dashboardType]);

  const COLORS = [
    '#3182CE', '#4299E1', '#63B3ED', '#90CDF4', 
    '#BEE3F8', '#EBF8FF', '#BEE3F8', '#90CDF4'
  ];

  if (data.length === 0) {
    return <div className="h-full flex items-center justify-center text-muted-foreground">Sem dados suficientes</div>;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: 10, right: 0, left: 0, bottom: 40 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="range" 
          angle={-45} 
          textAnchor="end" 
          height={80} 
          tick={{ fontSize: isMobile ? 9 : 12 }}
        />
        <YAxis 
          allowDecimals={false} 
          tick={{ fontSize: isMobile ? 10 : 12 }}
        />
        <Tooltip
          formatter={(value) => [`${value} ${value === 1 ? 'imóvel' : 'imóveis'}`, `Quantidade`]}
          labelFormatter={(label) => `Faixa: ${label}`}
        />
        <Bar dataKey="count" name="Quantidade" radius={[4, 4, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default PriceDistributionChart;
